/**
 * Goal Card Component
 *
 * Displays a single goal with progress bar and quick actions.
 * Goals represent desired outcomes (distinct from values which are about being).
 */

import { useState } from 'react';
import { format, isPast, differenceInDays } from 'date-fns';
import type { Goal } from '@/types';

interface GoalCardProps {
  goal: Goal;
  onComplete?: (goalId: string) => void;
  onReopen?: (goalId: string) => void;
  onDelete?: (goalId: string) => void;
  onEdit?: (goal: Goal) => void;
  onClick?: (goal: Goal) => void;
  onProgressUpdate?: (goalId: string, progress: number) => void;
  compact?: boolean;
  showChildren?: boolean;
}

const typeColors = {
  outcome: 'border-l-emerald-500 bg-emerald-50',
  process: 'border-l-blue-500 bg-blue-50',
  milestone: 'border-l-purple-500 bg-purple-50',
};

const typeLabels = {
  outcome: 'Outcome',
  process: 'Process',
  milestone: 'Milestone',
};

const typeIcons = {
  outcome: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  process: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  milestone: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
    </svg>
  ),
};

const statusColors = {
  active: 'bg-green-100 text-green-700',
  completed: 'bg-gray-100 text-gray-700',
  paused: 'bg-yellow-100 text-yellow-700',
  abandoned: 'bg-red-100 text-red-700',
};

export function GoalCard({
  goal,
  onComplete,
  onReopen,
  onDelete,
  onEdit,
  onClick,
  onProgressUpdate: _onProgressUpdate, // Reserved for future progress slider
  compact = false,
  showChildren = false,
}: GoalCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const isCompleted = goal.status === 'completed';
  const targetDate = goal.targetDate ? new Date(goal.targetDate) : null;
  const isOverdue = targetDate && isPast(targetDate) && !isCompleted;
  const isDueSoon = targetDate && differenceInDays(targetDate, new Date()) <= 7 && !isPast(targetDate);

  const handleCompleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCompleted && onReopen) {
      onReopen(goal.id);
    } else if (!isCompleted && onComplete) {
      onComplete(goal.id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(goal);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(goal.id);
    }
  };

  return (
    <div
      data-testid="goal-card"
      className={`
        relative border-l-4 rounded-lg shadow-sm transition-all
        ${typeColors[goal.goalType]}
        ${isCompleted ? 'opacity-60' : ''}
        ${onClick ? 'cursor-pointer hover:shadow-md' : ''}
        ${compact ? 'p-3' : 'p-4'}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick?.(goal)}
    >
      <div className="flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          {/* Type icon */}
          <div className={`flex-shrink-0 p-2 rounded-lg ${
            goal.goalType === 'outcome' ? 'bg-emerald-100 text-emerald-600' :
            goal.goalType === 'process' ? 'bg-blue-100 text-blue-600' :
            'bg-purple-100 text-purple-600'
          }`}>
            {typeIcons[goal.goalType]}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p
              data-testid="goal-title"
              className={`
                font-semibold text-gray-900
                ${isCompleted ? 'line-through text-gray-500' : ''}
                ${compact ? 'text-sm' : 'text-base'}
              `}
            >
              {goal.title}
            </p>

            {!compact && goal.description && (
              <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                {goal.description}
              </p>
            )}

            {/* Meta row */}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-500">
              {/* Target date */}
              {targetDate && (
                <span
                  className={`
                    inline-flex items-center gap-1
                    ${isOverdue ? 'text-red-600 font-medium' : ''}
                    ${isDueSoon && !isOverdue ? 'text-orange-600' : ''}
                  `}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  {isOverdue ? 'Overdue: ' : ''}
                  {format(targetDate, 'MMM d, yyyy')}
                </span>
              )}

              {/* Type badge */}
              <span
                className={`
                  px-1.5 py-0.5 rounded text-xs font-medium
                  ${goal.goalType === 'outcome' ? 'bg-emerald-100 text-emerald-700' : ''}
                  ${goal.goalType === 'process' ? 'bg-blue-100 text-blue-700' : ''}
                  ${goal.goalType === 'milestone' ? 'bg-purple-100 text-purple-700' : ''}
                `}
              >
                {typeLabels[goal.goalType]}
              </span>

              {/* Status badge (if not active) */}
              {goal.status !== 'active' && (
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusColors[goal.status]}`}>
                  {goal.status.charAt(0).toUpperCase() + goal.status.slice(1)}
                </span>
              )}

              {/* Value alignment count */}
              {goal.valueIds && goal.valueIds.length > 0 && (
                <span className="inline-flex items-center gap-1 text-primary-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {goal.valueIds.length} value{goal.valueIds.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Actions (shown on hover) */}
          {isHovered && (onComplete || onReopen || onEdit || onDelete) && (
            <div className="flex-shrink-0 flex items-center gap-1" data-testid="goal-actions">
              {!isCompleted && onComplete && (
                <button
                  data-testid="complete-goal-button"
                  onClick={handleCompleteClick}
                  className="px-2 py-1 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded transition-colors"
                  aria-label="Mark as complete"
                >
                  Complete
                </button>
              )}
              {isCompleted && onReopen && (
                <button
                  data-testid="reopen-goal-button"
                  onClick={handleCompleteClick}
                  className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                  aria-label="Reopen goal"
                >
                  Reopen
                </button>
              )}
              {onEdit && (
                <button
                  data-testid="edit-goal-button"
                  onClick={handleEdit}
                  className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                  aria-label="Edit goal"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
              )}
              {onDelete && (
                <button
                  data-testid="delete-goal-button"
                  onClick={handleDelete}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  aria-label="Delete goal"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Progress bar */}
        {!compact && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Progress</span>
              <span className="font-medium text-gray-900">{Math.round(goal.progress)}%</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  goal.progress >= 100 ? 'bg-green-500' :
                  goal.progress >= 75 ? 'bg-emerald-500' :
                  goal.progress >= 50 ? 'bg-blue-500' :
                  goal.progress >= 25 ? 'bg-yellow-500' :
                  'bg-gray-400'
                }`}
                style={{ width: `${Math.min(goal.progress, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Key Results (if present) */}
        {!compact && goal.keyResults && goal.keyResults.length > 0 && (
          <div className="pt-2 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">Key Results</p>
            <ul className="space-y-1">
              {goal.keyResults.slice(0, 3).map((kr, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="flex-shrink-0 w-4 h-4 mt-0.5 rounded-full border border-gray-300 flex items-center justify-center text-xs">
                    {index + 1}
                  </span>
                  <span className="line-clamp-1">{kr}</span>
                </li>
              ))}
              {goal.keyResults.length > 3 && (
                <li className="text-xs text-gray-500">+{goal.keyResults.length - 3} more</li>
              )}
            </ul>
          </div>
        )}

        {/* Child goals preview */}
        {showChildren && goal.children && goal.children.length > 0 && (
          <div className="pt-2 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Sub-goals ({goal.children.length})</p>
            <div className="space-y-2">
              {goal.children.slice(0, 2).map((child) => (
                <GoalCard
                  key={child.id}
                  goal={child}
                  compact={true}
                  onClick={onClick}
                />
              ))}
              {goal.children.length > 2 && (
                <p className="text-xs text-gray-500 text-center">+{goal.children.length - 2} more sub-goals</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

